import { useEffect, useState } from 'react';
import { isUnitedKingdomCountry, isUnitedStatesCountry } from './investorPricingView';
import { allUkCities } from '../constants/ukLocations';

const cache = new Map<string, string[]>();

function cacheKey(country: string, state?: string): string {
  const c = country.trim().toLowerCase();
  const s = (state || '').trim().toLowerCase();
  return s ? `${c}::${s}` : c;
}

async function fetchCities(country: string, state?: string): Promise<string[]> {
  const params = new URLSearchParams({ country: country.trim() });
  const stateTrimmed = (state || '').trim();
  const url = stateTrimmed
    ? `https://countriesnow.space/api/v0.1/countries/state/cities/q?${params}&state=${encodeURIComponent(stateTrimmed)}`
    : `https://countriesnow.space/api/v0.1/countries/cities/q?${params}`;

  const res = await fetch(url);
  if (!res.ok) return [];
  const json = (await res.json()) as { error?: boolean; data?: unknown };
  if (json.error || !Array.isArray(json.data)) return [];
  return Array.from(
    new Set(
      json.data.filter((city): city is string => typeof city === 'string' && city.trim() !== '')
    )
  ).sort((a, b) => a.localeCompare(b));
}

/**
 * City names for a headquarters country.
 * UK uses the local list. US loads by state. Everyone else loads by country.
 */
export function useCountryCities(
  country: string,
  state?: string
): { cities: string[]; loading: boolean } {
  const name = country.trim();
  const region = (state || '').trim();
  const key = cacheKey(name, region);
  const needsState = isUnitedStatesCountry(name);

  const [cities, setCities] = useState<string[]>(() => {
    if (!name) return [];
    if (isUnitedKingdomCountry(name)) return allUkCities();
    if (needsState && !region) return [];
    return cache.get(key) || [];
  });
  const [loading, setLoading] = useState(() => {
    if (!name || isUnitedKingdomCountry(name)) return false;
    if (needsState && !region) return false;
    if (cache.has(key)) return false;
    return true;
  });

  useEffect(() => {
    if (!name) {
      setCities([]);
      setLoading(false);
      return;
    }
    if (isUnitedKingdomCountry(name)) {
      setCities(allUkCities());
      setLoading(false);
      return;
    }
    if (needsState && !region) {
      setCities([]);
      setLoading(false);
      return;
    }

    const cached = cache.get(key);
    if (cached) {
      setCities(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setCities([]);
    void fetchCities(name, needsState ? region : undefined)
      .then(list => {
        cache.set(key, list);
        if (!cancelled) setCities(list);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, name, needsState, region]);

  return { cities, loading };
}
